import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Grid
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import { useFormik } from 'formik';
import {
  updateCustomerPushCartApi
} from 'api';
import { setPatientsGroupCart } from 'redux/actions/quote';
import { useStyles } from '../../styles';
import { calculateSavings, transformItemsToProducts } from '../../helpers';
import QuoteListingsItemRow from './QuoteListingsItemRow';
import QuoteListingsGroupSelect from './QuoteListingsGroupSelect';
import QuoteListingsFooter from './QuoteListingsFooter';
import { createValidationSchema } from './validation';
import { handleSelectDataSource } from './utils/handleSelectDataSource';
import { calculateTotals } from './utils/calculateTotals';


const QuoteListings = (({handleCancel}) => {
  const dispatch = useDispatch();
  const { id } = useParams<{id?: string}>();
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef(null);
  const isPatientGroupActive = location.pathname.includes('/quoting/group/');
  const [isLoading, setIsLoading] = useState(true);
  const [calculatedTotals, setCalculatedTotals] = useState({});

  const {
    customerPushCart,
    quoteProductsByGroup,
    quoteGroupsLoading,
    quoteProductsLoading,
    quoteProducts,
    loadingCustomerPushCart,
    loadingQuoteProductSubscriptionOptions,
    subscriptionOptionsBySku,
    currentMarketingSegment,
    patientsGroupCart,
    currentQuoteCustomer,
    quoteGroupsAttributeData
  } = useSelector((state: IReduxState) => state.quote);

  const loading =
    quoteGroupsLoading ||
    quoteProductsLoading ||
    loadingCustomerPushCart ||
    loadingQuoteProductSubscriptionOptions;

  useEffect(() => {
    const pushCartData = isPatientGroupActive ? patientsGroupCart : customerPushCart?.selectedItems;
    const transformedProductsData = transformItemsToProducts(patientsGroupCart, quoteProducts, subscriptionOptionsBySku);

    if (pushCartData?.length !== 0) {
      const initialValues = {
        items: (pushCartData || []).map((item) => {
          const {
            sku,
            qty,
            product_option: {
              extension_attributes: {
                subscription_id,
                quotepro_discounts
              },
            },
            price: updatedPrice,
            custom_subscription_price
          } = item;

          const {
            product: {
              catalog_quotepro_groups,
              price,
              subscription_options,
              subscription_active,
              additional_subscription_price,
              savings
            },
          } = customerPushCart?.products[sku] || transformedProductsData[sku] || {};

          const groupForAllItems = quoteGroupsAttributeData?.options?.find((group) => group.label === 'All');
          const selectedItemGroup = catalog_quotepro_groups?.toString().split(',')[0] || groupForAllItems?.value || '';
          const fixedPrice = updatedPrice ? updatedPrice : price || 0;
          const discountType = quotepro_discounts?.price_type || '';

          const isSubscriptionActive = subscription_active == 1;
          const subscriptionFrequency = subscription_options?.find((option) => option.id === subscription_id)?.frequency_count || '';
          const discount = quotepro_discounts?.price || '';

          return {
            selectedItemGroup,
            fixedPrice,
            sku,
            qty: qty || 1,
            subscription_id: isSubscriptionActive ? subscription_id || '' : '',
            discountType,
            subscriptionFrequency,
            additional_subscription_price: additional_subscription_price || 0,
            discount,
            savings: calculateSavings(discountType, discount, fixedPrice, savings, qty),
            subscriptionDiscount: custom_subscription_price ? 'allCycles' : 'firstBill',
            custom_subscription_price: custom_subscription_price || null,
          };
        }),
      };

      handleSetValues(initialValues);
    } else {
      setIsLoading(false);
    }
  }, []);

  const validationSchema = useMemo(() => createValidationSchema(
    quoteProductsByGroup,
    customerPushCart,
    subscriptionOptionsBySku,
    handleSelectDataSource,
    isPatientGroupActive,
    currentMarketingSegment,
    calculatedTotals
  ), [
    quoteProductsByGroup,
    customerPushCart,
    subscriptionOptionsBySku,
    isPatientGroupActive,
    currentMarketingSegment,
    calculatedTotals
  ]);

  const handleSave = async (values) => {
    const selectedItems = values.items.map((item) => {
      const { sku, discountType, discount, qty, subscription_id, _id, fixedPrice, custom_subscription_price } = item;

      return {
        _id,
        sku,
        qty,
        price: fixedPrice,
        custom_subscription_price,
        product_option: {
          extension_attributes: {
            quotepro_discounts: {
              price_type: discountType,
              price: discount,
            },
            subscription_id
          },
        },
      };
    });

    if (isPatientGroupActive) {
      const updatedPatientsGroupCart = dispatch(setPatientsGroupCart(selectedItems));

      if (updatedPatientsGroupCart && currentMarketingSegment?.value) {
        history.push(`/quoting/group/${currentMarketingSegment.value}/quote-workspace/summary`);
      }
    } else {
      const pushCart = {
        ...customerPushCart,
        customer: currentQuoteCustomer,
        selectedItems,
      };

      const updatedPushCart = await updateCustomerPushCartApi({
        dispatch,
        pushCartId: customerPushCart._id,
        pushCart
      });

      if (updatedPushCart && selectedItems.length > 0) {
        history.push(`/quoting/profile/${id}/quote-workspace/summary`);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      selectedGroup: '',
      items: [
        {
          selectedItemGroup: '',
          sku: '',
          fixedPrice: 0,
          qty: 1,
          subscription_id: '',
          subscriptionDiscount: 'firstBill',
          custom_subscription_price: null,
          discountType: '',
          subscriptionFrequency: '',
          additional_subscription_price: 0,
          savings: 0,
          discount: '',
        },
      ],
    },
    onSubmit: handleSave,
    validationSchema
  });

  const {
    values,
    handleSubmit,
    setValues,
    errors,
    touched
  } = formik;

  useEffect(() => {
    setCalculatedTotals(calculateTotals(values.items, customerPushCart, quoteProducts));
  }, [values.items]);

  useEffect(() => {
    if (values.items.length) {
      ref.current?.scrollTo({
        top: ((values.items.length + 1) * 49),
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [
    values.items.length,
    touched?.items?.[values?.items.length - 1],
    errors?.items?.[values?.items.length - 1]
  ]);

  const handleAddMoreItem = () => {
    const { items } = values;
    const updatedItems = [...items];
    const lastItemGroup = updatedItems[updatedItems.length - 1]?.selectedItemGroup || '';

    const newItem = {
      selectedItemGroup: lastItemGroup,
      sku: '',
      fixedPrice: 0,
      qty: 1,
      subscription_id: '',
      subscriptionDiscount: 'firstBill',
      custom_subscription_price: null,
      discountType: '',
      subscriptionFrequency: '',
      additional_subscription_price: 0,
      savings: 0,
      discount: ''
    };

    updatedItems.push(newItem);

    const updatedValues = {
      ...values,
      items: updatedItems,
      selectedGroup: lastItemGroup,
    };

    setValues(updatedValues);
  };

  const handleSetValues = useCallback(async (initialValues) => {
    await setValues({ selectedGroup: '', items: initialValues.items });

    setTimeout(() => setIsLoading(false), 0);
  }, []);

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      {isLoading && (
        <Grid container spacing={2} justifyContent="center" className="p-4">
          <CircularProgress color="inherit" size={20} />
        </Grid>
      )}
      {!isLoading && (
        <>
          <div className={classes.quoteGroupDotted}>
            <QuoteListingsGroupSelect
              formik={formik}
            />
            <div className="mt-2" ref={ref}>
              {values?.items && values?.items?.map((item, index) => (
                <QuoteListingsItemRow
                  item={item}
                  index={index}
                  formik={formik}
                  handleSave={handleSave}
                  key={`selectedItemId-${index}-${item._id}`}
                />
              ))}
            </div>
          </div>
          <QuoteListingsFooter
            formik={formik}
            calculatedTotals={calculatedTotals}
            loading={loading}
            handleAddMoreItem={handleAddMoreItem}
          />
        </>
      )}
      <div className="flex flex-row align-center justify-between mt-4">
        <Button
          variant="outlined"
          color="primary"
          className="uppercase"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Save
        </Button>
      </div>
      <BackdropCircular loading={isLoading || loading} />
    </form>
  );
});

export default QuoteListings;
